import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les LED sont les seules lumières.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'ledcube',
  family: 'grid',
  danceability: 0.82,
  requiresTier: 0,
  energyLevel: 0.78,
  bpmMin: 110,
  bpmMax: 170,
  movement: 0.70,
  tonality: 0.45,
  enabled: true
};

// LEDCUBE : un cube de LED géant de 16 x 16 x 16 (4 096 LED ; 12³ au tier 0)
// suspendu dans une salle noire, qui tourne lentement et joue des animations
// en VOLUME. Électro, techno, chiptune, EDM, house : preset party de vdjLook.
//
// L'ÉTAT est calculé sur le GPU à chaque image : un passage plein écran dans
// une cible 64 x 64 (les 16 tranches de 16 x 16 en mosaïque 4 x 4 ; 48 x 48 en
// 12³) écrit pour chaque LED sa couleur et sa luminosité. Les animations y
// sont des fonctions fermées du temps et de quelques uniformes : PLUIE
// (gouttes par colonne, rebond au fond), VAGUE (surface sinusoïdale, amplitude
// par les graves), SPHÈRE (coquille qui gonfle sur le kick), SPECTRE 3D (une
// petite texture N x N : bandes x historique, mise à jour toutes les 80 ms),
// TEXTE (le nom de l'artiste en glyphes 5 x 7 extrudés, sur la face tournée
// vers la caméra, qui défile), SERPENT (20 positions, pas de 70 ms), ÉCLAT
// (48 particules balistiques depuis un point), PLANS (balayage d'un axe).
//
// LE RENDU, cible privée MSAA : 4 096 sphères instanciées qui lisent l'état
// dans le vertex shader (allumée : émissive, cœur blanc ; éteinte : perle de
// verre sombre, Fresnel et reflet), des halos additifs instanciés qui
// s'éteignent avant leur bord (R12), les tiges verticales (un quad par
// colonne, aligné en espace écran, 1,2 px, éclairé par les LED qu'il porte),
// et le tout en miroir sous un sol noir brillant. Le cube occupe 60 % de la
// hauteur, centré (R19) ; la caméra tourne lentement, avec un à-coup de 10° sur
// la mesure.
//
// LE RYTHME : temps → coquille (SPHÈRE) ou balayage d'un plan en surimpression,
// séparation RGB ; mesure → animation suivante, toutes les LED clignotent une
// fois ; phrase → palette (monochrome, arc-en-ciel, deux couleurs) ; montée →
// PLANS de plus en plus rapides ; drop → toutes les LED en blanc puis ÉCLAT,
// flash (confort). Scène (lib/scene-clock.js, 32 s) : le TEXTE au moins une fois.

const ANIMS = ['rain', 'wave', 'sphere', 'spectrum', 'text', 'snake', 'burst', 'planes'];
const A = Object.fromEntries(ANIMS.map((n, i) => [n, i]));
const TIERS = { 0: { n: 12, msaa: 0 }, 1: { n: 16, msaa: 4 }, 2: { n: 16, msaa: 4 } };
const SIZE = 2.0;                        // the cube's edge, world units (centred on the origin)
const FLOOR_Y = -1.55;
const FOV_DEG = 40;
const SNAKE_LEN = 20;
const SCENE_S = 32;
const NO_BPM = 128;
const TEXT_SPEED = 9;                    // columns per second

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hashStr(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h >>> 0; }

// --- a 5 x 7 font, rows top to bottom, 5 bits each ---------------------------------------
const FONT = {
  A: [14, 17, 17, 31, 17, 17, 17], B: [30, 17, 17, 30, 17, 17, 30], C: [14, 17, 16, 16, 16, 17, 14], D: [28, 18, 17, 17, 17, 18, 28],
  E: [31, 16, 16, 30, 16, 16, 31], F: [31, 16, 16, 30, 16, 16, 16], G: [14, 17, 16, 23, 17, 17, 15], H: [17, 17, 17, 31, 17, 17, 17],
  I: [14, 4, 4, 4, 4, 4, 14], J: [7, 2, 2, 2, 2, 18, 12], K: [17, 18, 20, 24, 20, 18, 17], L: [16, 16, 16, 16, 16, 16, 31],
  M: [17, 27, 21, 21, 17, 17, 17], N: [17, 17, 25, 21, 19, 17, 17], O: [14, 17, 17, 17, 17, 17, 14], P: [30, 17, 17, 30, 16, 16, 16],
  Q: [14, 17, 17, 17, 21, 18, 13], R: [30, 17, 17, 30, 20, 18, 17], S: [15, 16, 16, 14, 1, 1, 30], T: [31, 4, 4, 4, 4, 4, 4],
  U: [17, 17, 17, 17, 17, 17, 14], V: [17, 17, 17, 17, 17, 10, 4], W: [17, 17, 17, 21, 21, 21, 10], X: [17, 17, 10, 4, 10, 17, 17],
  Y: [17, 17, 10, 4, 4, 4, 4], Z: [31, 1, 2, 4, 8, 16, 31],
  0: [14, 17, 19, 21, 25, 17, 14], 1: [4, 12, 4, 4, 4, 4, 14], 2: [14, 17, 1, 2, 4, 8, 31], 3: [31, 2, 4, 2, 1, 17, 14],
  4: [2, 6, 10, 18, 31, 2, 2], 5: [31, 16, 30, 1, 1, 17, 14], 6: [6, 8, 16, 30, 17, 17, 14], 7: [31, 1, 2, 4, 8, 8, 8],
  8: [14, 17, 17, 14, 17, 17, 14], 9: [14, 17, 17, 15, 1, 2, 12],
  ' ': [0, 0, 0, 0, 0, 0, 0], '-': [0, 0, 0, 31, 0, 0, 0], '!': [4, 4, 4, 4, 4, 0, 4], '.': [0, 0, 0, 0, 0, 12, 12],
  '&': [12, 18, 20, 8, 21, 18, 13], "'": [4, 4, 8, 0, 0, 0, 0], '?': [14, 17, 1, 2, 4, 0, 4]
};
// the text bitmap: one column per x (5 per glyph + 1 gap), 7 rows, row 0 at the BOTTOM
function textTexture(str) {
  const clean = String(str || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9 \-!.&'?]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 24) || 'HALOFLOOR';
  const w = clean.length * 6;
  const data = new Uint8Array(w * 7 * 4);
  for (let c = 0; c < clean.length; c++) {
    const g = FONT[clean[c]] || FONT[' '];
    for (let r = 0; r < 7; r++) for (let b = 0; b < 5; b++) {
      if (!((g[r] >> (4 - b)) & 1)) continue;
      const x = c * 6 + b, y = 6 - r, o = (y * w + x) * 4;
      data[o] = data[o + 1] = data[o + 2] = data[o + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, w, 7, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = tex.magFilter = THREE.NearestFilter; tex.needsUpdate = true;
  return { tex, w, text: clean };
}

// --- GLSL --------------------------------------------------------------------------------
const quadVS = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const stateFS = `
  precision highp float;
  uniform float uN, uTiles, uTex, uT, uAnim, uKickT, uPlaneT, uPlaneAxis, uOverT, uOverAxis, uBlink, uWhite, uBurstT, uPalMode, uHueT;
  uniform vec3 uBands, uBurstO, uC0, uC1, uC2, uTextH, uTextD;
  uniform sampler2D uText, uSpec;
  uniform float uTextW, uTextScroll, uSpecHead;
  uniform vec3 uSnake[${SNAKE_LEN}];
  varying vec2 vUv;
  float hash(vec2 p) { vec3 q = fract(vec3(p.xyx) * 0.1031); q += dot(q, q.yzx + 33.33); return fract((q.x + q.y) * q.z); }
  vec3 hash3(float n) { return vec3(hash(vec2(n, 1.7)), hash(vec2(n, 9.3)), hash(vec2(n, 4.1))); }
  float plane(vec3 u, float axis, float pos) { float c = axis < 0.5 ? u.x : axis < 1.5 ? u.y : u.z; float d = (c - pos) * uN; return exp(-d * d * 0.8); }
  vec3 hue(float h) { return clamp(abs(mod(h * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0) * 0.95 + 0.05; }
  void main() {
    vec2 px = floor(vUv * uTex);
    float x = mod(px.x, uN), y = mod(px.y, uN);
    float z = floor(px.y / uN) * uTiles + floor(px.x / uN);
    if (z >= uN) { gl_FragColor = vec4(0.0); return; }
    vec3 g = vec3(x, y, z), u = (g + 0.5) / uN;
    float v = 0.0, h = u.y;
    int a = int(uAnim + 0.5);
    if (a == 0) {                                   // RAIN
      float r = hash(g.xz), r2 = hash(g.xz + 7.3);
      if (r2 < 0.30 + 0.45 * uBands.z) {
        float ph = fract(uT / (0.9 + 0.8 * r) + r);
        float yd = 1.15 - ph * 1.6;
        bool falling = yd >= 0.0;
        if (!falling) yd = -yd * 0.35;
        float dy = (u.y - yd) * uN;
        v = exp(-dy * dy * 0.8);
        if (falling && dy > 0.0) v = max(v, 0.45 * exp(-dy * 0.6));
        h = r;
      }
    } else if (a == 1) {                            // WAVE
      float amp = 0.16 + 0.26 * uBands.x;
      float hg = 0.5 + amp * sin(u.x * 6.0 + uT * 1.8) * cos(u.z * 5.0 - uT * 1.3);
      float dy = (u.y - hg) * uN;
      v = exp(-dy * dy * 0.7); h = hg;
    } else if (a == 2) {                            // SPHERE: a shell from the centre on the kick
      float d = length(u - 0.5) * 2.0;
      float R = uKickT * 2.4;
      float dr = (d - R) * uN * 0.5;
      v = exp(-dr * dr * 1.5) * (1.0 - smoothstep(1.2, 1.8, R)); h = d * 0.6;
      v = max(v, exp(-d * d * 30.0) * 0.8);
    } else if (a == 3) {                            // SPECTRUM: bands across x, history along z
      float hg = texture2D(uSpec, vec2((x + 0.5) / uN, fract((z + uSpecHead) / uN + 0.5 / uN))).r;
      if (u.y < hg) v = 0.55 + 0.45 * step(hg - 1.0 / uN, u.y);
      h = u.y;
    } else if (a == 4) {                            // TEXT on the face toward the camera
      float hc = dot(u - 0.5, uTextH) + 0.5, dc = dot(u - 0.5, uTextD) + 0.5;
      float col = floor(hc * uN) + uTextScroll;
      float row = y - floor((uN - 7.0) * 0.5);
      // two layers deep, on the face toward the camera: seen face-on, the letters stay crisp
      if (col >= 0.0 && col < uTextW && row >= 0.0 && row < 7.0 && dc * uN > uN - 2.0) {
        v = texture2D(uText, vec2((col + 0.5) / uTextW, (row + 0.5) / 7.0)).r;
        h = col / uTextW;
      }
    } else if (a == 5) {                            // SNAKE
      for (int i = 0; i < ${SNAKE_LEN}; i++) {
        if (length(g - uSnake[i]) < 0.5) { float k = 1.0 - float(i) / ${(SNAKE_LEN + 6).toFixed(1)}; if (k > v) { v = k; h = float(i) / ${SNAKE_LEN.toFixed(1)}; } }
      }
    } else if (a == 7) {                            // PLANES
      v = plane(u, uPlaneAxis, fract(uPlaneT)); h = fract(uPlaneT);
    }
    // the burst (the BURST animation and the drop): 48 ballistic sparks
    if (uBurstT >= 0.0 && uBurstT < 1.8) {
      float fade = 1.0 - smoothstep(0.7, 1.7, uBurstT);
      for (int i = 0; i < 48; i++) {
        vec3 dir = normalize(hash3(float(i)) - 0.5 + 1e-3);
        float sp = 0.45 + 0.55 * hash(vec2(float(i), 3.1));
        vec3 p = uBurstO + dir * sp * uBurstT * 0.9 - vec3(0.0, 0.3 * uBurstT * uBurstT, 0.0);
        float dd = length(u - p) * uN;
        float b = exp(-dd * dd * 0.9) * fade;
        if (b > v) { v = b; h = float(i) / 48.0; }
      }
    }
    // the beat's plane sweep, over the other animations
    if (uOverT >= 0.0 && uOverT < 1.0) v = max(v, 0.4 * plane(u, uOverAxis, uOverT));
    v = max(v, uBlink * 0.7);
    vec3 col = uPalMode < 0.5 ? uC0 : uPalMode < 1.5 ? hue(fract(h * 0.8 + uHueT)) : mix(uC1, uC2, u.y);
    v = max(v, uWhite);
    col = mix(col, vec3(1.0), uWhite);
    gl_FragColor = vec4(col, clamp(v, 0.0, 1.0));
  }
`;
const STATE_CHUNK = `
  uniform sampler2D uState;
  uniform float uN, uTiles, uTex, uSize, uMirror, uFloorY;
  vec4 stateAt(vec3 g) { vec2 tile = vec2(mod(g.z, uTiles), floor(g.z / uTiles)); return texture2D(uState, (tile * uN + g.xy + 0.5) / uTex); }
  vec3 ledPos(vec3 g) { vec3 c = (g / (uN - 1.0) - 0.5) * uSize; if (uMirror > 0.5) c.y = 2.0 * uFloorY - c.y; return c; }
`;
const ledVS = `
  precision highp float;
  ${STATE_CHUNK}
  attribute vec3 aIJK;
  varying vec4 vS;
  varying vec3 vN, vV;
  varying float vFade;
  void main() {
    vec4 s = stateAt(aIJK);
    vec3 c = ledPos(aIJK);
    float sp = uSize / (uN - 1.0);
    float r = sp * mix(0.13, 0.27, smoothstep(0.05, 0.6, s.a));
    vec3 nn = normal;
    if (uMirror > 0.5) nn.y = -nn.y;
    vec3 w = c + vec3(position.x, uMirror > 0.5 ? -position.y : position.y, position.z) * r;
    vN = nn; vV = normalize(cameraPosition - w); vS = s;
    vFade = uMirror > 0.5 ? 0.22 * exp(-(uFloorY - w.y) * 0.0) * exp(-max(0.0, (c.y - 2.0 * uFloorY + uSize * 0.5)) * 0.0) : 1.0;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
  }
`;
const ledFS = `
  precision highp float;
  uniform float uGain;
  varying vec4 vS;
  varying vec3 vN, vV;
  varying float vFade;
  void main() {
    vec3 n = normalize(vN), v = normalize(vV);
    float ndv = max(dot(n, v), 0.0);
    // off: a dark glass bead - a faint body, a Fresnel rim, a small specular glint
    vec3 L = normalize(vec3(-0.4, 0.8, 0.45));
    float spec = pow(max(dot(reflect(-L, n), v), 0.0), 48.0);
    vec3 glass = vec3(0.018, 0.02, 0.025) + vec3(0.07, 0.085, 0.11) * pow(1.0 - ndv, 3.0) + vec3(0.35) * spec;
    // on: the colour emitted, a white core at the centre (R1: only the core over the bloom threshold)
    float on = vS.a;
    vec3 lit = vS.rgb * (0.75 + 0.35 * ndv) * on + vec3(1.0, 0.97, 0.93) * pow(ndv, 3.0) * smoothstep(0.35, 1.0, on) * 0.85;
    vec3 col = mix(glass, lit + glass * 0.3, smoothstep(0.02, 0.25, on));
    gl_FragColor = vec4(col * vFade * uGain, 1.0);
  }
`;
const haloVS = `
  precision highp float;
  ${STATE_CHUNK}
  attribute vec3 aIJK;
  varying vec2 vQ;
  varying vec4 vS;
  varying float vFade;
  void main() {
    vec4 s = stateAt(aIJK);
    vec3 c = ledPos(aIJK);
    float sp = uSize / (uN - 1.0);
    vec4 vc = viewMatrix * vec4(c, 1.0);
    float k = s.a < 0.05 ? 0.0 : sp * (0.9 + 0.7 * s.a);
    vc.xy += position.xy * 2.0 * k;
    vQ = position.xy * 2.0; vS = s;
    vFade = uMirror > 0.5 ? 0.18 : 1.0;
    gl_Position = projectionMatrix * vc;
  }
`;
const haloFS = `
  precision highp float;
  uniform float uGain;
  varying vec2 vQ;
  varying vec4 vS;
  varying float vFade;
  void main() {
    float r = length(vQ);
    float g = exp(-r * 5.5) * (1.0 - smoothstep(0.6, 0.95, r));
    gl_FragColor = vec4(vS.rgb * vS.a * g * 0.55 * vFade * uGain, 1.0);
  }
`;
// the rods: one quad per column, both ends projected, mixed in NDC, half width in pixels across
const rodVS = `
  precision highp float;
  ${STATE_CHUNK}
  attribute vec2 aXZ;
  uniform vec2 uRes;
  uniform float uPxW;
  varying float vSide;
  varying vec3 vCol;
  void main() {
    float s = position.y + 0.5;                       // 0 at the bottom LED, 1 above the top one
    vec3 A = ledPos(vec3(aXZ.x, 0.0, aXZ.y)), B = ledPos(vec3(aXZ.x, uN - 1.0, aXZ.y)) + vec3(0.0, uSize * 0.18, 0.0);
    vec4 ca = projectionMatrix * viewMatrix * vec4(A, 1.0), cb = projectionMatrix * viewMatrix * vec4(B, 1.0);
    vec3 na = ca.xyz / ca.w, nb = cb.xyz / cb.w;
    vec3 nd = mix(na, nb, s);
    vec2 dir = normalize((nb.xy - na.xy) * uRes + 1e-6);
    vec2 perp = vec2(-dir.y, dir.x) / uRes * 2.0;
    vSide = position.x * 2.0;
    nd.xy += perp * (uPxW + 1.0) * vSide;
    gl_Position = vec4(nd, 1.0);
    // lit by the LED it carries at this height
    float j = clamp(floor(s * (uN - 1.0) * 1.18 + 0.5), 0.0, uN - 1.0);
    vec4 st = stateAt(vec3(aXZ.x, j, aXZ.y));
    vCol = vec3(0.032, 0.034, 0.04) + st.rgb * st.a * 0.30;
  }
`;
const rodFS = `
  precision highp float;
  uniform float uPxW, uGain;
  varying float vSide;
  varying vec3 vCol;
  void main() {
    float dpx = abs(vSide) * (uPxW + 1.0);
    float a = 1.0 - smoothstep(uPxW - 0.6, uPxW + 0.6, dpx);
    if (a < 0.01) discard;
    gl_FragColor = vec4(vCol * a * uGain, a);
  }
`;
const displayFS = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uAspect;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;
    vec2 qq = abs((vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) - uSafeCenter) - uSafeHalfSize;
    float dz = length(max(qq, 0.0)) + min(max(qq.x, qq.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, dz));
    gl_FragColor = vec4(col, 1.0);
  }
`;

function instancedLEDs(base, n) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index; g.setAttribute('position', base.attributes.position);
  if (base.attributes.normal) g.setAttribute('normal', base.attributes.normal);
  const ijk = new Float32Array(n * n * n * 3);
  let k = 0;
  for (let z = 0; z < n; z++) for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) { ijk[k++] = x; ijk[k++] = y; ijk[k++] = z; }
  g.setAttribute('aIJK', new THREE.InstancedBufferAttribute(ijk, 3));
  g.instanceCount = n * n * n;
  return g;
}
function instancedRods(base, n) {
  const g = new THREE.InstancedBufferGeometry();
  g.index = base.index; g.setAttribute('position', base.attributes.position);
  const xz = new Float32Array(n * n * 2);
  let k = 0;
  for (let z = 0; z < n; z++) for (let x = 0; x < n; x++) { xz[k++] = x; xz[k++] = z; }
  g.setAttribute('aXZ', new THREE.InstancedBufferAttribute(xz, 2));
  g.instanceCount = n * n;
  return g;
}

export class LedcubeVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIERS[o.qualityTier] ? o.qualityTier : 1;
    this._T = TIERS[this._tier];
    const cs = parseInt(urlParam('cubesize'), 10);
    this._n = Number.isFinite(cs) ? Math.max(4, Math.min(20, cs)) : this._T.n;
    const fa = urlParam('cubeanim');
    this._forceAnim = fa && ANIMS.includes(fa) ? fa : null;
    this._adapt = new Adapt();
    this._post = new VdjPost({ bloomStrength: 0.45, bloomThreshold: 0.78, grain: 0.01, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    if (urlParam('cubeprobe') === '1' && typeof window !== 'undefined') window.__ledcube = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const n = this._n;
    this._tiles = Math.ceil(Math.sqrt(n));
    this._texSize = n * this._tiles;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._cam = new THREE.PerspectiveCamera(FOV_DEG, 1.78, 0.1, 100);
    this._private = new THREE.Scene();
    this._stateRT = new THREE.WebGLRenderTarget(this._texSize, this._texSize, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._specData = new Uint8Array(n * n * 4);
    this._specTex = new THREE.DataTexture(this._specData, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._specTex.minFilter = this._specTex.magFilter = THREE.NearestFilter; this._specTex.needsUpdate = true;
    this._text = textTexture('');
    this._SU = {
      uN: { value: n }, uTiles: { value: this._tiles }, uTex: { value: this._texSize }, uT: { value: 0 }, uAnim: { value: 0 }, uKickT: { value: 9 },
      uPlaneT: { value: 0 }, uPlaneAxis: { value: 0 }, uOverT: { value: -1 }, uOverAxis: { value: 0 }, uBlink: { value: 0 }, uWhite: { value: 0 }, uBurstT: { value: -1 },
      uPalMode: { value: 0 }, uHueT: { value: 0 }, uBands: { value: new THREE.Vector3(0.4, 0.3, 0.3) }, uBurstO: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
      uC0: { value: new THREE.Color() }, uC1: { value: new THREE.Color() }, uC2: { value: new THREE.Color() },
      uTextH: { value: new THREE.Vector3(1, 0, 0) }, uTextD: { value: new THREE.Vector3(0, 0, 1) },
      uText: { value: this._text.tex }, uSpec: { value: this._specTex }, uTextW: { value: this._text.w }, uTextScroll: { value: -100 }, uSpecHead: { value: 0 },
      uSnake: { value: Array.from({ length: SNAKE_LEN }, () => new THREE.Vector3(-100, -100, -100)) }
    };
    this._stateScene = new THREE.Scene();
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._SU, vertexShader: quadVS, fragmentShader: stateFS, depthTest: false, depthWrite: false }));
    sq.frustumCulled = false; this._stateScene.add(sq);

    const common = { uState: { value: this._stateRT.texture }, uN: { value: n }, uTiles: { value: this._tiles }, uTex: { value: this._texSize }, uSize: { value: SIZE }, uFloorY: { value: FLOOR_Y }, uGain: { value: 1 } };
    this._common = common;
    const sphere = new THREE.SphereGeometry(1, 12, 9), quad = new THREE.PlaneGeometry(1, 1), rodGeo = new THREE.PlaneGeometry(1, 1, 1, Math.max(1, n - 1));
    this._geos = [sphere, quad, rodGeo];
    this._meshes = [];
    const add = (geo, vs, fs, mirror, opts, order, extra) => {
      const m = new THREE.Mesh(geo, new THREE.ShaderMaterial(Object.assign({ uniforms: Object.assign({}, common, { uMirror: { value: mirror } }, extra || {}), vertexShader: vs, fragmentShader: fs }, opts)));
      m.frustumCulled = false; m.renderOrder = order; this._private.add(m); this._meshes.push(m); return m;
    };
    const additive = { transparent: true, blending: THREE.AdditiveBlending, depthWrite: false };
    const ledsGeo = instancedLEDs(sphere, n), halosGeo = instancedLEDs(quad, n);
    add(ledsGeo, ledVS, ledFS, 1, {}, 1);
    add(halosGeo, haloVS, haloFS, 1, additive, 2);
    this._rodU = { uRes: { value: new THREE.Vector2(1600, 900) }, uPxW: { value: 0.6 } };
    add(instancedRods(rodGeo, n), rodVS, rodFS, 0, { transparent: true, depthWrite: false, side: THREE.DoubleSide }, 3, this._rodU);
    add(ledsGeo, ledVS, ledFS, 0, {}, 4);
    add(halosGeo, haloVS, haloFS, 0, additive, 5);

    this._target = null;
    this._dispU = { uTex: { value: null }, uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._dispU, vertexShader: quadVS, fragmentShader: displayFS, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._seed = 9; this._trackSeen = undefined; this._artist = null;
    this._time = 0; this._freePhase = 0; this._beats = 0; this._beatsC = 0;
    this._anim = this._forceAnim || 'wave'; this._textStart = -1; this._textDone = false; this._textScene = -1;
    this._kickAt = -9; this._overAt = -9; this._overAxis = 0; this._blink = 0; this._white = 0; this._burstAt = -9; this._burstO = new THREE.Vector3(0.5, 0.5, 0.5);
    this._planeT = 0; this._planeAxis = 0; this._palMode = 0; this._palShift = 0; this._build = 0; this._intensity = 0.4;
    this._yaw = 0.6; this._yawKick = 0; this._yawKickTarget = 0;
    this._snake = []; for (let i = 0; i < SNAKE_LEN; i++) this._snake.push([Math.floor(n / 2), Math.floor(n / 2), Math.floor(n / 2)]);
    this._snakeDir = [1, 0, 0]; this._snakeAcc = 0;
    this._specAcc = 0; this._specHead = 0;
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
    this._log = [];
    this._setAnim(this._anim);
  }

  _rnd() { this._seed = (Math.imul(this._seed, 1664525) + 1013904223) >>> 0; return this._seed / 4294967296; }
  _note(e) { e.t = +this._time.toFixed(2); this._log.push(e); if (this._log.length > 30) this._log.shift(); }

  _colours(profile) {
    const pal = profile.artworkPalette;
    const src = (pal && (pal.vividColors || pal.dominantColors)) || [];
    const hsl = [];
    for (let k = 0; k < 3; k++) {
      const c = _tone[k];
      if (src.length) vdjColor(src[(k + this._palShift) % src.length], c); else c.setHSL((0.55 + k * 0.33 + this._palShift * 0.1) % 1, 0.9, 0.55);
      const o = { h: 0, s: 0, l: 0 }; c.getHSL(o); hsl.push(o);
    }
    for (let k = 1; k < 3; k++) for (let j = 0; j < k; j++) { const d = Math.abs(hsl[k].h - hsl[j].h); if (Math.min(d, 1 - d) < 0.25) { hsl[k].h = (hsl[0].h + k / 3) % 1; _tone[k].setHSL(hsl[k].h, 0.9, 0.55); } }
    this._SU.uC0.value.copy(_tone[0]); this._SU.uC1.value.copy(_tone[1]); this._SU.uC2.value.copy(_tone[2]);
  }

  // the face toward the camera: the text reads left to right on it
  _textFace() {
    // the nearest face-on angle: the cube turns to it while the text runs
    this._yawFace = Math.round((this._yaw + this._yawKick) / (Math.PI / 2)) * (Math.PI / 2) - this._yawKick;
    const th = this._yawFace + this._yawKick, s = Math.sin(th), c = Math.cos(th);
    if (Math.abs(c) >= Math.abs(s)) { this._SU.uTextH.value.set(Math.sign(c), 0, 0); this._SU.uTextD.value.set(0, 0, Math.sign(c)); }
    else { this._SU.uTextH.value.set(0, 0, -Math.sign(s)); this._SU.uTextD.value.set(Math.sign(s), 0, 0); }
  }
  _setAnim(name) {
    this._anim = name;
    if (name === 'text') { this._textStart = this._time; this._textFace(); this._textScene = Math.floor(this._clock.elapsed / SCENE_S); }
    this._note({ what: 'anim', name });
  }
  _nextAnim() {
    if (this._forceAnim) return;
    const sceneIdx = Math.floor(this._clock.elapsed / SCENE_S);
    if (this._textScene !== sceneIdx && this._clock.t > 14) { this._setAnim('text'); return; }   // the name at least once per scene
    const pool = ANIMS.filter((a) => a !== this._anim && a !== 'text');
    this._setAnim(pool[Math.floor(this._rnd() * pool.length)]);
  }

  _stepSnake() {
    const n = this._n, head = this._snake[0];
    const dirs = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
    const ok = (d) => { const p = [head[0] + d[0], head[1] + d[1], head[2] + d[2]]; return p.every((v) => v >= 0 && v < n) && !this._snake.slice(0, 6).some((q) => q[0] === p[0] && q[1] === p[1] && q[2] === p[2]); };
    let d = this._snakeDir;
    if (this._rnd() < 0.22 || !ok(d)) {
      const cand = dirs.filter((x) => !(x[0] === -d[0] && x[1] === -d[1] && x[2] === -d[2]) && ok(x));
      if (cand.length) d = cand[Math.floor(this._rnd() * cand.length)];
    }
    this._snakeDir = d;
    const p = [Math.max(0, Math.min(n - 1, head[0] + d[0])), Math.max(0, Math.min(n - 1, head[1] + d[1])), Math.max(0, Math.min(n - 1, head[2] + d[2]))];
    this._snake.unshift(p); this._snake.pop();
  }

  _stepSpectrum(b, t) {
    const n = this._n, row = this._specHead % n;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      const lvl = u < 0.5 ? b.low * (1 - u * 2) + b.mid * u * 2 : b.mid * (2 - u * 2) + b.high * (u * 2 - 1);
      const jit = 0.7 + 0.6 * (0.5 + 0.5 * Math.sin(t * (3 + i * 0.7) + i * 1.9));
      this._specData[(row * n + i) * 4] = Math.round(255 * Math.min(1, Math.max(0.05, lvl * 1.4 * jit)));
    }
    this._specHead++;
    this._specTex.needsUpdate = true;
    this._SU.uSpecHead.value = this._specHead % n;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    const playing = profile.playbackState === 'PLAYING';
    const a = this._adapt.update(playing ? dt : 0, profile);
    if (profile.trackId !== this._trackSeen) {
      this._trackSeen = profile.trackId;
      this._seed = hashStr(String(profile.title || '') + '|' + String(profile.artist || '')) || 9;
    }
    const artist = String(profile.artist || profile.title || '');
    if (artist !== this._artist) {
      this._artist = artist;
      if (this._text) this._text.tex.dispose();
      this._text = textTexture(artist);
      this._SU.uText.value = this._text.tex; this._SU.uTextW.value = this._text.w;
    }
    this._colours(profile);
    if (!playing) return;                                       // frozen, never black
    this._time += dt;
    this._clock.update(dt);
    this._intensity += (a.intensity - this._intensity) * Math.min(1, dt / 2);
    const I = this._intensity, t = this._time;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    const beatS = 60 / bpm;
    const pulsed = a.pulse > 0.25;
    let beat = a.beat, phase;
    if (pulsed) phase = a.beatPhase;
    else { this._freePhase += dt / beatS; if (this._freePhase >= 1) { this._freePhase -= 1; beat = true; } phase = this._freePhase; }
    if (beat) this._beats++;
    this._beatsC = this._beats + phase;
    const bar = a.bar || (!pulsed && beat && this._beats % 4 === 0);
    const phrase = a.phrase || (!pulsed && beat && this._beats % 16 === 0);
    const b = a.bands || { low: 0.3 + 0.4 * I, mid: 0.3 + 0.2 * I, high: 0.2 + 0.3 * I };
    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * Math.min(1, dt / 0.8);
    const U = this._SU;

    // the text runs to its end, whatever the bars say
    const textLeft = this._anim === 'text' && (t - this._textStart) * TEXT_SPEED < this._text.w + this._n + 2;
    if (this._anim === 'text' && !textLeft) { if (this._forceAnim) this._setAnim('text'); else this._nextAnim(); }
    // the bar: the next animation, every LED blinks once; the climb forces the planes
    if (bar) {
      if (this._anim !== 'text') this._yawKickTarget += 10 * Math.PI / 180;
      if (!textLeft) {
        if (this._build > 0.4 && !this._forceAnim) { if (this._anim !== 'planes') this._setAnim('planes'); }
        else this._nextAnim();
        this._blink = 1;
      }
    }
    if (phrase) { this._palMode = (this._palMode + 1) % 3; this._palShift = (this._palShift + 1) % 3; }
    // the beat: a shell (SPHERE), a burst (BURST), or a plane sweep over the others
    if (beat) {
      if (this._anim === 'sphere') this._kickAt = t;
      else if (this._anim === 'burst') { this._burstAt = t; this._burstO.set(0.25 + 0.5 * this._rnd(), 0.35 + 0.4 * this._rnd(), 0.25 + 0.5 * this._rnd()); }
      else if (this._anim !== 'planes' && this._anim !== 'text') { this._overAt = t; this._overAxis = (this._overAxis + 1) % 3; }
    }
    // the drop: every LED white, then a burst from the centre, a flash
    if (a.drop) {
      this._white = 1; this._dropBurstAt = t + 0.25;
      if (director && this._comfort.requestFlash(t)) director.flash = VDJ_DROP_FLASH;
      this._note({ what: 'drop' });
    }
    if (this._dropBurstAt != null && t >= this._dropBurstAt) { this._burstAt = t; this._burstO.set(0.5, 0.5, 0.5); this._dropBurstAt = null; }
    this._white = Math.max(0, this._white - dt / 0.25);
    this._blink = Math.max(0, this._blink - dt / 0.08);
    // the planes: a sweep per beat, faster through the climb
    const rate = 1 + 3 * Math.max(0, this._build);
    const prevPT = this._planeT;
    this._planeT += dt / beatS * rate * 0.5;
    if (Math.floor(this._planeT) !== Math.floor(prevPT)) this._planeAxis = (this._planeAxis + 1) % 3;
    // the snake and the spectrum
    this._snakeAcc += dt; while (this._snakeAcc > 0.07) { this._snakeAcc -= 0.07; this._stepSnake(); }
    this._specAcc += dt; while (this._specAcc > 0.08) { this._specAcc -= 0.08; this._stepSpectrum(b, t); }
    for (let i = 0; i < SNAKE_LEN; i++) U.uSnake.value[i].set(this._snake[i][0], this._snake[i][1], this._snake[i][2]);

    U.uT.value = t; U.uAnim.value = A[this._anim]; U.uKickT.value = t - this._kickAt;
    U.uPlaneT.value = this._planeT; U.uPlaneAxis.value = this._planeAxis;
    const ot = (t - this._overAt) / beatS; U.uOverT.value = ot < 1 ? ot : -1; U.uOverAxis.value = this._overAxis;
    U.uBlink.value = this._blink; U.uWhite.value = this._white;
    U.uBurstT.value = t - this._burstAt < 1.8 ? t - this._burstAt : -1; U.uBurstO.value.copy(this._burstO);
    U.uPalMode.value = this._palMode; U.uHueT.value = t * 0.05;
    U.uBands.value.set(b.low, b.mid, b.high);
    U.uTextScroll.value = this._anim === 'text' ? Math.floor((t - this._textStart) * TEXT_SPEED) - this._n : -100;
    // the rotation: slow, slower while the text runs, a 10 degree jolt on the bar (eased)
    if (this._anim === 'text') this._yaw += (this._yawFace - this._yaw) * Math.min(1, dt / 0.6);
    else this._yaw += dt * 0.22;
    this._yawKick += (this._yawKickTarget - this._yawKick) * Math.min(1, dt / 0.25);
    this._common.uGain.value = Math.min(1, a.lumaCap / 0.42);
    this._post.update(vdjBeatChroma(phase, beatS));
  }

  // the cube: 60 % of the height (its bounding sphere), centred on the stage; 12 degrees above
  _placeCamera(aspect) {
    const cam = this._cam, tanH = Math.tan(FOV_DEG / 2 * Math.PI / 180);
    const R = 1.22 * SIZE / 2;                  // the silhouette's half height at a 12 degree view, not the bounding sphere
    const want = Math.min(0.6 * tanH, 0.85 * tanH * aspect);
    const dist = R / Math.sin(Math.atan(want));
    const el = 0.21, th = this._yaw + this._yawKick;
    cam.position.set(dist * Math.cos(el) * Math.sin(th), dist * Math.sin(el), dist * Math.cos(el) * Math.cos(th));
    cam.up.set(0, 1, 0); cam.lookAt(0, 0, 0); cam.updateMatrixWorld();
  }

  _render(renderer) {
    const cur = renderer.getRenderTarget();
    let w, h;
    if (cur) { w = cur.width; h = cur.height; } else { const bsz = renderer.getDrawingBufferSize(_size); w = bsz.x; h = bsz.y; }
    const cam = this._cam;
    if (Math.abs(cam.aspect - w / h) > 1e-4) { cam.aspect = w / h; cam.updateProjectionMatrix(); }
    this._placeCamera(w / h);
    this._rodU.uRes.value.set(w, h); this._rodU.uPxW.value = 0.6 * Math.max(1, h / 1080);
    if (!this._target || this._target.width !== w || this._target.height !== h) {
      if (this._target) this._target.dispose();
      const msaa = renderer.capabilities.isWebGL2 ? this._T.msaa : 0;
      this._target = new THREE.WebGLRenderTarget(w, h, { type: THREE.UnsignedByteType, depthBuffer: true, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, samples: msaa });
    }
    const cc = renderer.getClearColor(_cc), ca = renderer.getClearAlpha();
    const xr = renderer.xr.enabled; renderer.xr.enabled = false;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this._stateRT); renderer.render(this._stateScene, this._ortho);
    renderer.setRenderTarget(this._target); renderer.clear(true, true, false); renderer.render(this._private, cam);
    renderer.setRenderTarget(cur);
    renderer.setClearColor(cc, ca);
    renderer.xr.enabled = xr;
    this._dispU.uTex.value = this._target.texture;
  }

  dispose() {
    if (this._scene && this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
    if (this._display) { this._display.geometry.dispose(); this._display.material.dispose(); }
    for (const m of this._meshes || []) { m.geometry.dispose(); m.material.dispose(); }
    for (const g of this._geos || []) g.dispose();
    for (const r of [this._stateRT, this._target]) if (r) r.dispose();
    if (this._specTex) this._specTex.dispose();
    if (this._text) this._text.tex.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__ledcube === this) window.__ledcube = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
const _tone = [new THREE.Color(), new THREE.Color(), new THREE.Color()];
